// BuildGhosts — "(building)" rows under the scene list during a Live Motion Build
// (spec §5.6). UI-only: fed by BuildChat through buildMode's ghost store, never Redux.
import React from "react";
import i18n from "i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AgentCluster } from "./BuildWaiting";
import { ghostItems, useBuildGhosts } from "./buildMode";

const EXIT = { opacity: 0, height: 0, transition: { duration: 0.35, ease: [0.2, 0.8, 0.2, 1] } };

export default function BuildGhosts({ realIds }) {
  const items = ghostItems(useBuildGhosts(), realIds);
  const reduce = useReducedMotion();
  const offset = (realIds || []).length; // ghosts continue the real rows' numbering
  const liveIndex = useBuildGhosts().live?.index;
  return (
    <AnimatePresence initial={false}>
      {items.map((g, k) => (
        <motion.div
          key={g.navId}
          className="file navItemBlock aidea-build-ghost"
          aria-disabled="true"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduce ? { opacity: 0, transition: { duration: 0 } } : EXIT}
        >
          <div className="item-number">{offset + k + 1}</div>
          <div className="item-divider" />
          <span className="aidea-build-ghost-title">
            {g.title}
            <em>{i18n.t("aideaChat.build.ghost")}</em>
            {g.index === liveIndex && <i className="aidea-build-dot" />}
          </span>
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

// Canvas overlay while the selected scene is not in the doc yet (nothing selected = id 0).
// Same ghost store; the shimmer is CSS and stops under prefers-reduced-motion.
export function BuildCanvasOverlay({ selectedId, realIds }) {
  const g = useBuildGhosts();
  const reduce = useReducedMotion();
  const ready = (realIds || []).some((id) => id !== 0 && id === selectedId);
  if (g.done || ready || !(g.planned || []).length) return null;
  const live = g.live;
  const phase = live
    ? live.step === "saving"
      ? "saved"
      : live.phase || (live.step ? "generate" : "plan")
    : null;
  return (
    <div className="aidea-build-canvas" role="status" aria-live="polite">
      <AgentCluster compact stage={g.live ? "scene" : undefined} reduce={reduce} />
      <div className="aidea-build-skel">
        <div className="aidea-build-skel-bar aidea-build-skel-title" />
        <div className="aidea-build-skel-bar aidea-build-skel-img" />
        <div className="aidea-build-skel-bar aidea-build-skel-line" />
        <div className="aidea-build-skel-bar aidea-build-skel-line short" />
      </div>
      <div className="aidea-build-canvas-status">
        <strong>
          {live
            ? i18n.t("aideaChat.build.canvas.scene", {
                n: live.index + 1,
                total: g.total || g.planned.length,
              })
            : i18n.t("aideaChat.build.step.waiting")}
        </strong>
        {phase && <span>{i18n.t(`aideaChat.build.canvas.phase.${phase}`)}</span>}
        <div className="aidea-build-canvas-progress" />
      </div>
    </div>
  );
}
